import { createContext, useContext, useMemo, useState } from "react";
import { matchPath, useLocation } from "react-router";
import { BREADCRUMBS_MAP } from "./BREADCRUMBS_MAP";

export type Breadcrumb = {
  label: string;
  to?: string;
};

type BreadcrumbsContextValue = {
  breadcrumbs: Breadcrumb[] | null;
  setBreadcrumbs: (items: Breadcrumb[] | null) => void;
  clearBreadcrumbs: () => void;
  resolvedBreadcrumbs: Breadcrumb[];
};

export const BreadcrumbsContext = createContext<BreadcrumbsContextValue | null>(null);

export const BreadcrumbsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[] | null>(null);

  const routeBreadcrumbs = useMemo(() => {
    const matchEntry = BREADCRUMBS_MAP.find(({ path }) => matchPath({ path, end: true }, location.pathname));

    if (!matchEntry) return [];

    const match = matchPath({ path: matchEntry.path, end: true }, location.pathname);
    if (!match) return [];

    const params = match.params;

    return matchEntry.breadcrumbs.map((b) => ({
      label: b.label.replace(/:(\w+)/g, (_, key) => params[key] ?? ""),
      to: b.to ? b.to.replace(/:(\w+)/g, (_, key) => params[key] ?? "") : undefined,
    }));
  }, [location.pathname]);

  const fromChain = useMemo(() => {
    const chain: string[] = [];
    let from = new URLSearchParams(location.search).get("from");

    while (from && !chain.includes(from)) {
      chain.push(from);
      from = new URLSearchParams(from.split("?")[1] ?? "").get("from");
    }

    return chain;
  }, [location.search]);

  const resolvedBreadcrumbs = useMemo(
    () =>
      (breadcrumbs ?? routeBreadcrumbs).map((b) => ({
        ...b,
        to: b.to ? (fromChain.find((url) => url.split("?")[0] === b.to) ?? b.to) : undefined,
      })),
    [breadcrumbs, fromChain, routeBreadcrumbs]
  );

  return (
    <BreadcrumbsContext.Provider
      value={{
        breadcrumbs,
        setBreadcrumbs,
        clearBreadcrumbs: () => setBreadcrumbs(null),
        resolvedBreadcrumbs,
      }}
    >
      {children}
    </BreadcrumbsContext.Provider>
  );
};

export const useBreadcrumbs = () => {
  const ctx = useContext(BreadcrumbsContext);
  if (!ctx) throw new Error("useBreadcrumbs must be used inside BreadcrumbsProvider");

  return ctx.resolvedBreadcrumbs;
};

export const useBreadcrumbsController = () => {
  const ctx = useContext(BreadcrumbsContext);
  if (!ctx) throw new Error("useBreadcrumbsController must be used inside BreadcrumbsProvider");

  return {
    set: ctx.setBreadcrumbs,
    clear: ctx.clearBreadcrumbs,
  };
};
